import { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import { getErrorMessage } from "@/lib/errorMessage";
// Self-service password change for a logged-in user (must supply current password).
export function ChangePasswordDialog({ open, onOpenChange }) {
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setCur(""); setPw(""); setPw2(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("New passwords do not match."); return; }
    if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: pw });
      toast.success("Password updated.");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not change password"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="bg-[#121212] border-zinc-800 rounded-sm" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">Change my password</DialogTitle>
          <DialogDescription className="text-zinc-500">Enter your current password, then choose a new one (at least 8 characters).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="label-tag" htmlFor="cur">Current password</Label>
            <PasswordInput id="cur" testId="current-password-input" value={cur} onChange={(e) => setCur(e.target.value)}
              required className="mt-1.5 bg-secondary border-zinc-800 rounded-sm" />
          </div>
          <div>
            <Label className="label-tag" htmlFor="np">New password</Label>
            <PasswordInput id="np" testId="new-password-input" value={pw} onChange={(e) => setPw(e.target.value)}
              required className="mt-1.5 bg-secondary border-zinc-800 rounded-sm" />
          </div>
          <div>
            <Label className="label-tag" htmlFor="np2">Confirm new password</Label>
            <PasswordInput id="np2" testId="confirm-password-input" value={pw2} onChange={(e) => setPw2(e.target.value)}
              required className="mt-1.5 bg-secondary border-zinc-800 rounded-sm" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-800 rounded-sm">Cancel</Button>
            <Button type="submit" data-testid="change-password-submit" disabled={busy} className="bg-[#D5001C] hover:bg-[#B90018] rounded-sm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
